/*
 * Copyright (C) 2026 Discorch Contributors
 *
 * This file is part of Discorch.
 *
 * Discorch is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Discorch is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Discorch. If not, see <https://www.gnu.org/licenses/>.
 */

import { CopyButton } from "@/components/CopyButton";
import { CheckboxWithState } from "@/components/studio/MessageView/CheckboxWithState";
import { MessageCard } from "@/components/studio/MessageView/MessageCard";
import { MessageFilters } from "@/components/studio/MessageView/MessageFilters";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { useSearch } from "@/hooks/useSearch";
import { getChannelTypeLabel } from "@/lib/constants";
import { sanitizeForSearch } from "@/lib/process-package";
import {
  getSingleQuotedPhrase,
  matchesSearchQuery,
  parseSearchQuery,
} from "@/lib/search-query";
import { useSelection } from "@/lib/stores/selection";
import type { Message, MessageFilterState, ProcessedChannel } from "@/types";
import { useVirtualizer } from "@tanstack/react-virtual";
import { format } from "date-fns";
import {
  Calendar,
  MessagesSquare,
  Paperclip,
  Search,
  User,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const OVERSCAN_COUNT = 5;
const SEARCH_CONTEXT_SIZE = 2;

interface MessageViewProps {
  selectedChannel?: ProcessedChannel;
  onClose?: () => void;
}

interface ViewItem {
  messageId: string;
  index: number;
  isMatch?: boolean;
  isContext?: boolean;
}

export function MessageView({ selectedChannel, onClose }: MessageViewProps) {
  const [localSearch, setLocalSearch] = useState("");
  const [localFilters, setLocalFilters] = useState<MessageFilterState>({});
  const [searchMatchCount, setSearchMatchCount] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const regularListRef = useRef<HTMLDivElement>(null);
  const searchListRef = useRef<HTMLDivElement>(null);
  const { filters: globalFilters } = useSearch();
  const { toggleChannel, getChannelSelectionState } = useSelection();

  const highlightSearchTerm = useMemo(() => {
    const singleQuoted = getSingleQuotedPhrase(localSearch);
    return singleQuoted ?? localSearch;
  }, [localSearch]);

  const hasMeaningfulSearchQuery = useCallback((raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return false;

    const parsed = parseSearchQuery(trimmed);
    if (parsed.hasQuotes) {
      return parsed.exactPhrases.length > 0 || parsed.looseTerms.length > 0;
    }

    return sanitizeForSearch(trimmed).length > 0;
  }, []);

  const filteredMessages = useMemo(() => {
    if (!selectedChannel?.messages) return [];

    const CHUNK_SIZE = 1000;
    const result: typeof selectedChannel.messages = [];

    const fromTimeGlobal =
      globalFilters.dateRange?.from?.getTime() ?? -Infinity;
    const toTimeGlobal = globalFilters.dateRange?.to?.getTime() ?? Infinity;
    const fromTimeLocal = localFilters.dateRange?.from?.getTime() ?? -Infinity;
    const toTimeLocal = localFilters.dateRange?.to?.getTime() ?? Infinity;

    for (let i = 0; i < selectedChannel.messages.length; i += CHUNK_SIZE) {
      const chunk = selectedChannel.messages.slice(i, i + CHUNK_SIZE);

      for (const message of chunk) {
        const messageDate = new Date(message.Timestamp).getTime();

        const passesDateFilters =
          messageDate >= fromTimeGlobal &&
          messageDate <= toTimeGlobal &&
          messageDate >= fromTimeLocal &&
          messageDate <= toTimeLocal;

        const passesAttachmentFilters =
          (!globalFilters.hasAttachments || message.Attachments) &&
          (!localFilters.hasAttachments || message.Attachments);

        if (passesDateFilters && passesAttachmentFilters) {
          result.push(message);
        }
      }
    }

    return result;
  }, [
    selectedChannel,
    globalFilters.dateRange?.from,
    globalFilters.dateRange?.to,
    globalFilters.hasAttachments,
    localFilters.dateRange?.from,
    localFilters.dateRange?.to,
    localFilters.hasAttachments,
  ]);

  const searchResults = useMemo(() => {
    if (!isSearchMode || !filteredMessages.length) {
      setSearchMatchCount(0);
      return [];
    }

    const searchTerm = localSearch.toLowerCase();
    const parsed = parseSearchQuery(localSearch);
    const useQuotedSemantics = parsed.hasQuotes;
    const CHUNK_SIZE = 500;
    const matches: ViewItem[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < filteredMessages.length; i += CHUNK_SIZE) {
      const chunk = filteredMessages.slice(i, i + CHUNK_SIZE);

      for (let j = 0; j < chunk.length; j++) {
        const message = chunk[j];
        const actualIndex = i + j;

        const contents = message.Contents ?? "";
        const isMatch = useQuotedSemantics
          ? matchesSearchQuery(sanitizeForSearch(contents), parsed)
          : contents.toLowerCase().includes(searchTerm);

        if (isMatch) {
          for (
            let k = Math.max(0, actualIndex - SEARCH_CONTEXT_SIZE);
            k < actualIndex;
            k++
          ) {
            const contextMsg = filteredMessages[k];
            if (!seen.has(contextMsg.ID)) {
              seen.add(contextMsg.ID);
              matches.push({
                messageId: contextMsg.ID,
                index: k,
                isContext: true,
              });
            }
          }

          seen.add(message.ID);
          matches.push({
            messageId: message.ID,
            index: actualIndex,
            isMatch: true,
          });

          for (
            let k = actualIndex + 1;
            k <=
            Math.min(
              filteredMessages.length - 1,
              actualIndex + SEARCH_CONTEXT_SIZE,
            );
            k++
          ) {
            const contextMsg = filteredMessages[k];
            if (!seen.has(contextMsg.ID)) {
              seen.add(contextMsg.ID);
              matches.push({
                messageId: contextMsg.ID,
                index: k,
                isContext: true,
              });
            }
          }
        }
      }
    }

    setSearchMatchCount(matches.filter((m) => m.isMatch).length);
    return matches.sort((a, b) => a.index - b.index);
  }, [filteredMessages, localSearch, isSearchMode]);

  const filteredMessageMap = useMemo(() => {
    const map = new Map<string, Message>();
    for (const message of filteredMessages) {
      map.set(message.ID, message);
    }
    return map;
  }, [filteredMessages]);

  const searchHitMessages = useMemo(() => {
    if (!isSearchMode) return [];

    return searchResults
      .filter((item) => item.isMatch)
      .map((item) => filteredMessageMap.get(item.messageId))
      .filter((message): message is Message => Boolean(message));
  }, [isSearchMode, searchResults, filteredMessageMap]);

  const activeMessages = useMemo(
    () => (isSearchMode ? searchHitMessages : filteredMessages),
    [isSearchMode, searchHitMessages, filteredMessages],
  );

  const selectionState = selectedChannel
    ? getChannelSelectionState(selectedChannel.id, activeMessages)
    : { selected: false, indeterminate: false, count: 0 };

  const handleChannelSelect = useCallback(
    (checked: boolean) => {
      if (selectedChannel) {
        toggleChannel(selectedChannel.id, activeMessages, checked);
      }
    },
    [selectedChannel, toggleChannel, activeMessages],
  );

  const regularVirtualizer = useVirtualizer({
    count: filteredMessages.length,
    getScrollElement: () => regularListRef.current,
    estimateSize: useCallback(() => 100, []),
    overscan: OVERSCAN_COUNT,
  });

  const searchVirtualizer = useVirtualizer({
    count: searchResults.length,
    getScrollElement: () => searchListRef.current,
    estimateSize: useCallback(() => 100, []),
    overscan: OVERSCAN_COUNT,
  });

  const navigateToSearchResult = useCallback(
    (direction: "next" | "previous") => {
      const matches = searchResults.filter((item) => item.isMatch);
      if (!matches.length) return;

      const newIndex =
        direction === "next"
          ? (currentSearchIndex + 1) % matches.length
          : (currentSearchIndex - 1 + matches.length) % matches.length;

      setCurrentSearchIndex(newIndex);

      const targetItem = matches[newIndex];
      const virtualIndex = searchResults.findIndex(
        (item) => item.messageId === targetItem.messageId,
      );

      searchVirtualizer.scrollToIndex(virtualIndex, {
        align: "center",
        behavior: "smooth",
      });
    },
    [searchResults, currentSearchIndex, searchVirtualizer],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearch(value);
      setIsSearchMode(hasMeaningfulSearchQuery(value));
      setCurrentSearchIndex(0);
    },
    [hasMeaningfulSearchQuery],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (e.shiftKey) {
          navigateToSearchResult("previous");
        } else {
          navigateToSearchResult("next");
        }
      }
    },
    [navigateToSearchResult],
  );

  const handleSearchClear = useCallback(() => {
    setLocalSearch("");
    setIsSearchMode(false);
    setSearchMatchCount(0);
    setCurrentSearchIndex(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setLocalFilters({});
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      setLocalSearch(globalFilters.terms);
      setIsSearchMode(hasMeaningfulSearchQuery(globalFilters.terms));
      setCurrentSearchIndex(0);
    }
  }, [selectedChannel, globalFilters.terms, hasMeaningfulSearchQuery]);

  const viewKey = useMemo(() => {
    return [
      selectedChannel?.id,
      localSearch,
      isSearchMode,
      localFilters.dateRange?.from?.getTime(),
      localFilters.dateRange?.to?.getTime(),
      localFilters.hasAttachments,
      globalFilters.dateRange?.from?.getTime(),
      globalFilters.dateRange?.to?.getTime(),
      globalFilters.hasAttachments,
    ].join("|");
  }, [
    selectedChannel?.id,
    localSearch,
    isSearchMode,
    localFilters,
    globalFilters,
  ]);

  if (!selectedChannel) {
    return (
      <div className="bg-card flex min-h-[20rem] flex-1 items-center justify-center rounded-lg border p-4">
        <div className="text-muted-foreground flex flex-col items-center gap-2">
          <MessagesSquare className="h-8 w-8" />
          <p className="text-center">Select a channel to view messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border">
      <div className="flex min-h-[6rem] flex-col gap-3 border-b p-4">
        <div className="grid items-center gap-4 sm:grid-cols-[1fr_auto] md:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
          <div className="flex min-w-0 items-center gap-2">
            <CheckboxWithState
              checked={selectionState.selected}
              indeterminate={selectionState.indeterminate}
              onCheckedChange={handleChannelSelect}
              aria-label={
                isSearchMode
                  ? `Select all matching messages in ${selectedChannel.displayName}`
                  : `Select all messages in ${selectedChannel.displayName}`
              }
              className="shrink-0"
            />
            <h2 className="truncate font-medium">
              {selectedChannel.displayName}
            </h2>
            <Badge variant="outline" className="shrink-0 font-mono text-xs">
              {selectedChannel.id}
            </Badge>
            {searchMatchCount > 0 && (
              <Badge variant="secondary" className="shrink-0">
                {searchMatchCount} match{searchMatchCount !== 1 ? "es" : ""}
              </Badge>
            )}
          </div>

          <div className="flex w-full items-center gap-2">
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                value={localSearch}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search within channel..."
                className="w-full pr-8 pl-9"
              />
              {localSearch && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-0 right-0 h-full px-3"
                  onClick={handleSearchClear}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            <MessageFilters
              filters={localFilters}
              onFiltersChange={setLocalFilters}
              onClearFilters={handleClearFilters}
              className="shrink-0"
            />

            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                title="Close view"
                className="shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        <Separator className="my-0" />

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge
            variant="secondary"
            className="inline-flex shrink-0 items-center gap-1"
          >
            {getChannelTypeLabel(selectedChannel.type)}
          </Badge>
          <Badge
            variant="outline"
            className="inline-flex shrink-0 items-center gap-1"
          >
            <MessagesSquare className="h-3.5 w-3.5" />
            <span>
              {filteredMessages.length.toLocaleString()} message
              {filteredMessages.length !== 1 ? "s" : ""}
            </span>
          </Badge>
          {selectedChannel.attachmentCount > 0 && (
            <Badge
              variant="outline"
              className="inline-flex shrink-0 items-center gap-1"
            >
              <Paperclip className="h-3.5 w-3.5" />
              <span>
                {selectedChannel.attachmentCount.toLocaleString()} attachment
                {selectedChannel.attachmentCount !== 1 ? "s" : ""}
              </span>
            </Badge>
          )}
          <Badge
            variant="outline"
            className="inline-flex shrink-0 items-center gap-1"
          >
            <Calendar className="h-3.5 w-3.5" />
            <span>
              {format(selectedChannel.dateRange.start, "PP")} -{" "}
              {format(selectedChannel.dateRange.end, "PP")}
            </span>
          </Badge>
          {selectedChannel.type === "DM" && selectedChannel.recipientId && (
            <div className="flex items-center gap-1.5">
              <Badge
                variant="outline"
                className="inline-flex shrink-0 items-center gap-1"
              >
                <User className="h-3.5 w-3.5" />
                <span className="font-mono">{selectedChannel.recipientId}</span>
              </Badge>
              <CopyButton
                text={selectedChannel.recipientId}
                variant="outline"
                size="sm"
                className="h-6 px-2 py-0 text-xs"
              />
            </div>
          )}
        </div>
      </div>

      {isSearchMode ? (
        <div
          key={`search-${viewKey}`}
          ref={searchListRef}
          className="flex-1 overflow-auto overscroll-contain"
        >
          <div
            style={{
              height: `${searchVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {searchVirtualizer.getVirtualItems().map((virtualRow) => {
              const item = searchResults[virtualRow.index];
              const message = filteredMessages[item.index];

              return (
                <div
                  key={message.ID}
                  data-index={virtualRow.index}
                  data-message-id={message.ID}
                  ref={searchVirtualizer.measureElement}
                  className="absolute top-0 left-0 w-full"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <MessageCard
                    channel={selectedChannel}
                    message={message}
                    searchTerm={highlightSearchTerm}
                    isMatch={item.isMatch}
                    isContext={item.isContext}
                    selectable={false}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          key={`regular-${viewKey}`}
          ref={regularListRef}
          className="flex-1 overflow-auto overscroll-contain"
        >
          <div
            style={{
              height: `${regularVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {regularVirtualizer.getVirtualItems().map((virtualRow) => {
              const message = filteredMessages[virtualRow.index];

              return (
                <div
                  key={message.ID}
                  data-index={virtualRow.index}
                  data-message-id={message.ID}
                  ref={regularVirtualizer.measureElement}
                  className="absolute top-0 left-0 w-full"
                  style={{
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <MessageCard
                    channel={selectedChannel}
                    message={message}
                    searchTerm={highlightSearchTerm}
                    selectable={true}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
